import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { listUrgentNotifications, ackUrgentNotification, triggerEmergencyTransport } from "@/lib/api";

const POLL_MS = 30000;

// Lote 2 — sensory feedback: tiny base64-encoded 440Hz "ding" (~250ms).
// Inline so we don't need to ship an asset file.
const DING_DATA_URI =
  "data:audio/wav;base64,UklGRl4DAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YToDAAAAAB4SIyL2D6jp3sQVtVbDC+ywHU48PicgSADHvVOnHKvDxwIDLDFmLDsRJfQg32rWStxk7+wHzhWnFmEEDPL93jHVbtV24Iv3aQq7G98i5BlDB/PtbtfKx7DGItb86d4FtBjkIPwbCgxk93nlYNn22HzlefnTDfgcVCNUHAML5/QQ4ojSvMnvz5Tig/eZDXMb1iJsHDcK//Kr4DvSwM+B22HvqgYxGZQg0Btn"
  + "Cuf2v+UA1+/V8N7v7yYHJxk0H+0YlAfQ8Tff5tN10qfd3+0/AnUUhxk5FXgIcfes6BPbcdU92TXq2vy7DBcXrhfPDmsBN/Mj57jdMtt23wPpRfdwBlEThxk5FXgIcfes6BPbcdU92TXq2vy7DBcXrhfPDmsBN/Mj57jdMtt23wPpRfdwBlET";

const VIB_PATTERN = [200, 80, 200];

/**
 * Polls /api/notifications/urgent every 30 s. If any unacknowledged urgent
 * alerts exist, displays a BLOCKING modal (cannot close with ESC / outside
 * click) until the user acknowledges them one by one.
 */
const UrgentAlertModal = ({ enabled = true }) => {
  const { t } = useI18n();
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const seenIds = useRef(new Set());
  const audioRef = useRef(null);

  // Home Guarantee state
  const [emergencyData, setEmergencyData] = useState(null);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyError, setEmergencyError] = useState(null);


  useEffect(() => {
    if (typeof Audio !== "undefined") {
      audioRef.current = new Audio(DING_DATA_URI);
      audioRef.current.volume = 0.55;
    }
  }, []);

  const triggerSensoryFeedback = useCallback(() => {
    try {
      audioRef.current?.play?.().catch(() => { /* autoplay blocked */ });
    } catch { /* noop */ }
    try {
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        navigator.vibrate(VIB_PATTERN);
      }
    } catch { /* noop */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await listUrgentNotifications();
      const list = Array.isArray(data) ? data : [];
      // Detect newly-arrived urgents (id not seen yet) and fire feedback once.
      const fresh = list.some((n) => !seenIds.current.has(n.id));
      list.forEach((n) => seenIds.current.add(n.id));
      if (fresh && list.length > 0) {
        triggerSensoryFeedback();
      }
      setPending(list);
    } catch {
      /* keep current state on error */
    }
  }, [enabled, triggerSensoryFeedback]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    if (pending.length > 0) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [pending.length]);

  if (!enabled || pending.length === 0) return null;
  const current = pending[0];

  const handleAck = async () => {
    setBusyId(current.id);
    try {
      await ackUrgentNotification(current.id);
      setPending((prev) => prev.filter((n) => n.id !== current.id));
      // Reset emergency state for the next alert if any
      setEmergencyData(null);
      setEmergencyError(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="urgent-alert-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        data-testid="urgent-alert-modal"
        className="w-full max-w-md bg-white border-2 border-red-600 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95"
      >
        <div className="bg-red-600 px-5 py-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-white" />
          <div className="text-white font-bold tracking-wide uppercase text-sm">
            {t("urgent.banner")}
          </div>
          {pending.length > 1 && (
            <span
              data-testid="urgent-alert-counter"
              className="ml-auto text-[0.75rem] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full"
            >
              1 / {pending.length}
            </span>
          )}
        </div>
        <div className="px-6 py-6 text-center">
          <h2 className="text-lg font-bold text-red-700 mb-2" data-testid="urgent-alert-title">
            {t(current.title_key, current.body_params || {})}
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="urgent-alert-body">
            {t(current.body_key, current.body_params || {})}
          </p>

          {/* Home Guarantee Flow */}
          {current.kind === "route_cancelled_by_driver" && current.route_id && !emergencyData && (
            <div className="mt-6 pt-4 border-t border-red-100 text-left">
              <h3 className="font-semibold text-slate-800 mb-1">{t("urgent.home_guarantee_title")}</h3>
              <p className="text-xs text-slate-500 mb-3">{t("urgent.home_guarantee_desc")}</p>
              
              {emergencyError && (
                <div className="bg-red-50 text-red-700 p-2 rounded-lg text-xs font-semibold mb-3">
                  {emergencyError}
                </div>
              )}

              <button
                type="button"
                onClick={async () => {
                  setEmergencyLoading(true);
                  setEmergencyError(null);
                  try {
                    const res = await triggerEmergencyTransport(current.route_id);
                    setEmergencyData(res);
                  } catch (err) {
                    setEmergencyError(err.response?.data?.detail || t("urgent.guarantee_error"));
                  } finally {
                    setEmergencyLoading(false);
                  }
                }}
                disabled={emergencyLoading}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-500 text-white font-semibold py-2.5 rounded-xl transition-colors shadow-sm"
              >
                {emergencyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "🚕"}
                {emergencyLoading ? t("urgent.processing") : t("urgent.request_guarantee")}
              </button>
            </div>
          )}

          {emergencyData && emergencyData.status === "PENDING_HR" && (
            // Sin proveedor de VTC integrado: se avisa a RRHH y se dice la
            // verdad (antes salía un «vale» inventado y un coche que no venía)
            <div data-testid="guarantee-hr-notified" className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
              <p className="text-amber-800 font-bold text-base mb-1">{t("urgent.hr_notified")}</p>
              <p className="text-xs text-slate-700 leading-relaxed font-medium">{t("urgent.hr_notified_desc")}</p>
              <div className="mt-3 text-[0.6875rem] text-slate-400 flex justify-between border-t border-amber-100 pt-2">
                <span>Ref: {emergencyData.emergency_id.split("-")[0]}</span>
                <span>CC: {emergencyData.centro_coste}</span>
              </div>
            </div>
          )}

          {emergencyData && emergencyData.status === "APPROVED" && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl text-left">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-green-700 font-bold text-lg">{t("urgent.vtc_confirmed")}</span>
              </div>
              <div className="bg-white p-3 rounded-lg border border-green-100 shadow-sm mb-3 text-center">
                <p className="text-[0.6875rem] uppercase font-bold text-slate-400 mb-1">{t("urgent.voucher_code")}</p>
                <p className="text-xl font-black text-slate-800 font-mono tracking-widest">{emergencyData.vtc_voucher_code}</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed font-medium">
                {emergencyData.pickup_instructions}
              </p>
              <div className="mt-3 text-[0.6875rem] text-slate-400 flex justify-between border-t border-green-100 pt-2">
                <span>Ref: {emergencyData.emergency_id.split("-")[0]}</span>
                <span>CC: {emergencyData.centro_coste}</span>
              </div>
            </div>
          )}

          {emergencyData && emergencyData.status === "RESCUED_BY_CARPOOL" && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl text-left">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-blue-700 font-bold text-lg">{t("urgent.peer_found")}</span>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-100 shadow-sm mb-3">
                <p className="text-sm text-slate-700 leading-relaxed font-medium">
                  {emergencyData.pickup_instructions}
                </p>
              </div>
              <p className="text-[0.75rem] text-slate-500 mt-2">
                {t("urgent.peer_found_desc")}
              </p>
            </div>
          )}
        </div>
        <div className="px-6 pb-6">
          <button
            type="button"
            data-testid="urgent-alert-ack-button"
            onClick={handleAck}
            disabled={busyId === current.id || emergencyLoading}
            className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 rounded-xl shadow-sm transition-colors"
          >
            {busyId === current.id && <Loader2 className="h-4 w-4 animate-spin" />}
            {emergencyData ? t("urgent.close_view_map") : t("urgent.ack")}
          </button>
          <p className="text-[0.75rem] text-slate-400 text-center mt-3">{t("urgent.footer")}</p>
        </div>
      </div>
    </div>
  );
};

export default UrgentAlertModal;
